export type PublicSessionUser = {
  id: string | null;
  name: string | null;
  email: string | null;
  image: string | null;
  plan: string | null;
  provider: string | null;
  isAdmin: boolean;
};

export type PublicSession = {
  user: PublicSessionUser;
};

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

export function normalizePublicSession(payload: unknown): PublicSession | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const userValue = (payload as { user?: unknown }).user;
  if (!userValue || typeof userValue !== 'object') {
    return null;
  }

  const user = userValue as Record<string, unknown>;
  const normalizedUser: PublicSessionUser = {
    id: readString(user.id),
    name: readString(user.name),
    email: readString(user.email),
    image: readString(user.image),
    plan: readString(user.plan),
    provider: readString(user.provider),
    isAdmin: Boolean(user.isAdmin),
  };

  if (!normalizedUser.id && !normalizedUser.email) {
    return null;
  }

  return { user: normalizedUser };
}

export function hasAuthenticatedPublicSession(session: PublicSession | null): boolean {
  return Boolean(session?.user.id || session?.user.email);
}

export async function fetchPublicSession(fetcher: typeof fetch = fetch): Promise<PublicSession | null> {
  const response = await fetcher('/api/auth/session', {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json().catch(() => null)) as unknown;
  return normalizePublicSession(payload);
}
